import { Circle, Container, Graphics, Rectangle, Sprite, Text, type Texture } from 'pixi.js';
import { BoardPanel, COLOURS, DISPLAY_FONT, FLOOR_SHADE, displayStyle, floorFill, uiStyle } from '../board';

const BRASS = 0xd9a441;

/** Display text, centred on its origin. */
export function displayText(value: string, size: number, fill: number = COLOURS.gold): Text {
  const t = new Text({ text: value, style: displayStyle(size, fill) });
  t.anchor.set(0.5);
  return t;
}

function iconSprite(texture: Texture | null, size: number): Sprite | null {
  if (!texture) return null;
  const s = new Sprite(texture);
  s.anchor.set(0.5);
  s.scale.set(size / Math.max(texture.width, texture.height));
  return s;
}

/**
 * A small name plate on a world's floor, drawn into `g`: a drop shadow, the
 * floor faded under the boards' shade (darker when `dim`) and an edge. It
 * paints the floor into the shape, with no mask, so the map's dozens of
 * plates stay cheap. Before the floor arrives it is the boards' dark base.
 */
export function floorPlate(
  g: Graphics,
  rect: { x: number; y: number; w: number; h: number },
  floor: Texture | null,
  look: { dim?: boolean; edge: number; edgeWidth?: number },
): Graphics {
  const { x, y, w, h } = rect;
  g.roundRect(x, y + 3, w, h, 8)
    .fill({ color: 0x000000, alpha: 0.4 })
    .roundRect(x, y, w, h, 8)
    .fill({ color: COLOURS.floor });
  if (floor) g.roundRect(x, y, w, h, 8).fill(floorFill(floor));
  return g
    .roundRect(x, y, w, h, 8)
    .fill({ color: FLOOR_SHADE.color, alpha: look.dim ? 0.74 : FLOOR_SHADE.alpha })
    .stroke({ color: look.edge, width: look.edgeWidth ?? 1.5 });
}

export interface PlaqueArt {
  lock: Texture | null;
  gem: Texture | null;
  /** The stars' picture (a plate with stars draws its own without it). */
  star?: Texture | null;
  /** The floor of the slot's world (it can come later: {@link RealmPlaque.setFloor}). */
  floor?: Texture | null;
}

/** A plate's second line (the world maps' plates and worlds). */
export interface PlaqueMore {
  /** Stars earned, 0 to 3, at the start of the second line. */
  stars?: number;
  /** Words on the second line, after any stars (a gem price follows them
   *  on a locked slot's plate). */
  line?: string;
  /** The widest it may be, in screen pixels: its words shrink to fit. */
  maxWidth?: number;
  /** A narrow two-line plate: the name alone on the first line; the lock,
   *  gem and price (or the stars, with no words) on the second. */
  narrow?: boolean;
}

/** Stars a slot can earn. */
const STARS = 3;

/**
 * A painted building's name plate: its world's floor, faded under the
 * boards' shade, with a brass edge (producer 2026-10-19: the plates match
 * the land). A locked slot shows the lock and its gem price, on a darker
 * plate; the slot the player came from glows. A second line, when asked
 * for, holds the slot's stars and a few words (a world map's chapter line,
 * or when a locked slot opens free). Drawn at screen size: the map sets its
 * scale to 1 / zoom. Its origin is its top centre.
 */
export class RealmPlaque extends Container {
  private readonly plate = new Graphics();
  private readonly w: number;
  private readonly h: number;
  private floor: Texture | null = null;
  /** The second line's words, as shown (tests). */
  readonly line: string;
  /** The stars shown lit, or null with none drawn (tests). */
  readonly stars: number | null;

  constructor(
    name: string,
    private readonly open: boolean,
    price: number | null,
    private readonly here: boolean,
    art: PlaqueArt,
    more: PlaqueMore = {},
  ) {
    super();
    const lockRow: Container[] = [];
    const priceRow: Container[] = [];
    if (!open) {
      const lock = iconSprite(art.lock, 15);
      if (lock) lockRow.push(lock);
    }
    const nameText = displayText(name, 14, open ? 0xffe48a : 0xe8dcc4);
    if (!open && price) {
      const gem = iconSprite(art.gem, 14);
      if (gem) priceRow.push(gem);
      const p = new Text({
        text: String(price),
        style: {
          fontFamily: DISPLAY_FONT,
          fontSize: 13,
          fill: 0xbfe8ff,
          stroke: { color: 0x1a2a3a, width: 2 },
        },
      });
      p.anchor.set(0.5);
      priceRow.push(p);
    }
    // The wide plate: lock, name, gem and price on one line. The narrow one
    // puts the name on its own line and the lock, gem and price below it.
    const narrow = more.narrow === true;
    const top: Container[] = narrow ? [nameText] : [...lockRow, nameText, ...priceRow];
    const second: Container[] = narrow ? [...lockRow, ...priceRow] : [];
    this.stars = more.stars === undefined ? null : Math.max(0, Math.min(STARS, more.stars));
    if (this.stars !== null) {
      for (let i = 0; i < STARS; i++) second.push(starIcon(art.star ?? null, 11, i < this.stars));
    }
    this.line = narrow ? '' : (more.line ?? '');
    if (this.line) {
      const words = new Text({ text: this.line, style: uiStyle(11, 0xf3e7cf, '600') });
      words.anchor.set(0.5);
      second.push(words);
    }
    const gap = 5;
    const rowWidth = (parts: Container[], spacing: number): number =>
      parts.reduce((a, c) => a + c.width, 0) + spacing * Math.max(0, parts.length - 1);
    const pad = 20;
    const max = (more.maxWidth ?? Infinity) - pad;
    const rows: [Container[], number, number][] = [[top, gap, 0]];
    if (second.length) rows.push([second, 3, 0]);
    for (const row of rows) {
      const width = rowWidth(row[0], row[1]);
      // Too wide: the row's words shrink to fit (never below three quarters).
      row[2] = width > max ? Math.max(0.75, max / width) : 1;
    }
    const widest = Math.max(...rows.map(([parts, spacing, k]) => rowWidth(parts, spacing) * k));
    this.w = Math.min(widest + pad, (more.maxWidth ?? Infinity));
    this.h = second.length ? 42 : 24;
    this.addChild(this.plate);
    this.floor = art.floor ?? null;
    this.drawPlate();
    rows.forEach(([parts, spacing, k], r) => {
      const row = new Container();
      let x = 0;
      for (const c of parts) {
        c.position.set(x + c.width / 2, 0);
        row.addChild(c);
        x += c.width + spacing;
      }
      row.scale.set(k);
      row.position.set((-x + spacing) * k * 0.5, r === 0 ? (second.length ? 13 : 12) : 31);
      this.addChild(row);
    });
    this.hitArea = new Rectangle(-this.w / 2, 0, this.w, this.h);
  }

  /** The floor under the name, or null before it has arrived. */
  get floorTexture(): Texture | null {
    return this.floor;
  }

  /** The world's floor has arrived. */
  setFloor(texture: Texture | null): void {
    if (!texture || texture === this.floor || this.destroyed) return;
    this.floor = texture;
    this.drawPlate();
  }

  private drawPlate(): void {
    const { w, h, here, open } = this;
    const plate = this.plate.clear();
    if (here) {
      for (const [grow, alpha] of [
        [9, 0.18],
        [6, 0.3],
        [3, 0.55],
      ] as const) {
        plate
          .roundRect(-w / 2 - grow, -grow, w + grow * 2, h + grow * 2, 8 + grow)
          .fill({ color: 0xffd25a, alpha });
      }
    }
    // A locked slot's plate is a shade darker, as its name is muted.
    floorPlate(plate, { x: -w / 2, y: 0, w, h }, this.floor, {
      dim: !open,
      edge: here ? 0xffe48a : open ? BRASS : 0x8a7458,
      edgeWidth: here ? 2 : 1.5,
    });
  }
}

/** A star, lit or not: the painted star, or a drawn one before it loads.
 *  Never a star character (iOS Safari draws some as boxes). */
function starIcon(texture: Texture | null, size: number, lit: boolean): Container {
  const s = iconSprite(texture, size);
  if (s) {
    // An unlit star: the same star in muted stone, so all three read.
    if (!lit) s.tint = 0x7d705c;
    return s;
  }
  const g = new Graphics();
  const pts: number[] = [];
  for (let i = 0; i < 10; i++) {
    const r = i % 2 === 0 ? size / 2 : size / 4.6;
    const a = -Math.PI / 2 + (i * Math.PI) / 5;
    pts.push(Math.cos(a) * r, Math.sin(a) * r);
  }
  g.poly(pts).fill(lit ? 0xffc83a : 0x7d705c).stroke({ color: 0x2a1605, width: 1 });
  // Drawn about its middle, as the sprites are.
  const c = new Container();
  c.addChild(g);
  c.boundsArea = new Rectangle(-size / 2, -size / 2, size, size);
  return c;
}

/**
 * The HUD's crown, which opens the multiplayer Wheel: the crown medallion
 * (or a red disc before it loads) and its caption. `into` is emptied first.
 */
export function drawCrown(into: Container, texture: Texture | null): void {
  for (const c of into.removeChildren()) c.destroy({ children: true });
  const size = 58;
  const pic = iconSprite(texture, size);
  into.addChild(
    pic ?? new Graphics().circle(0, 0, size / 2).fill(0xc0392b).stroke({ color: BRASS, width: 3 }),
  );
  const caption = new Text({ text: 'Wheel', style: uiStyle(11, 0xffffff) });
  caption.anchor.set(0.5, 0);
  caption.position.set(0, size / 2 + 1);
  into.addChild(caption);
  into.hitArea = { contains: (x: number, y: number) => Math.hypot(x, y - 6) <= size / 2 + 8 };
}

/** A world's name, lettered on the painting (screen size, like the plates). */
export function worldLabel(name: string): Text {
  const t = new Text({
    text: name.toUpperCase(),
    style: {
      fontFamily: DISPLAY_FONT,
      fontSize: 21,
      letterSpacing: 2,
      fill: 0xfff6dc,
      stroke: { color: 0x4a2c10, width: 3 },
      dropShadow: { color: 0x321e0a, alpha: 0.75, blur: 8, distance: 2, angle: Math.PI / 2 },
    },
  });
  t.anchor.set(0.5);
  return t;
}

/** A round brass button: the light bezel with a picture in it, and a caption. */
export class BezelButton extends Container {
  constructor(
    bezel: Texture | null,
    picture: Texture | Text | null,
    caption: string | null,
    onTap: () => void,
    /** How wide the picture is drawn: the icons' 26, larger for a painted picture that fills the well (the map). */
    pictureSize = 26,
  ) {
    super();
    const r = 24;
    const well = new Graphics()
      .circle(0, 3, r)
      .fill({ color: 0x000000, alpha: 0.35 })
      .circle(0, 0, r)
      .fill(0x2a1605);
    this.addChild(well);
    if (picture instanceof Text) {
      this.addChild(picture);
    } else {
      const pic = iconSprite(picture, pictureSize);
      if (pic) this.addChild(pic);
    }
    const ring = iconSprite(bezel, r * 2 + 4);
    if (ring) this.addChild(ring);
    else this.addChild(new Graphics().circle(0, 0, r).stroke({ color: BRASS, width: 4 }));
    if (caption) {
      const t = new Text({
        text: caption,
        style: uiStyle(11, 0xffffff),
      });
      t.anchor.set(0.5, 0);
      t.position.set(0, r + 3);
      this.addChild(t);
    }
    this.eventMode = 'static';
    this.cursor = 'pointer';
    this.hitArea = new Circle(0, caption ? 6 : 0, r + 8);
    this.on('pointertap', onTap);
  }
}

/**
 * The world switcher along the bottom: the arrows step west and east
 * through the worlds; the world's name opens its list of slots. It sits on
 * the floor of the world it names ({@link setFloor} as that changes).
 */
export class RealmSwitcher extends Container {
  readonly prev: Container;
  readonly next: Container;
  readonly centre = new Container();
  private readonly panel: BoardPanel;
  private readonly title: Text;
  private readonly sub: Text;
  private w = 380;

  constructor(
    floor: Texture | null,
    arrow: Texture | null,
    handlers: { prev: () => void; next: () => void; open: () => void },
  ) {
    super();
    this.panel = new BoardPanel({ floor });
    this.addChild(this.panel);
    const arrowButton = (flip: boolean, onTap: () => void): Container => {
      const c = new Container();
      const pic = iconSprite(arrow, 30) ?? displayText(flip ? '▶' : '◀', 22);
      if (flip) pic.scale.x *= -1;
      c.addChild(pic);
      c.eventMode = 'static';
      c.cursor = 'pointer';
      c.hitArea = new Rectangle(-24, -24, 48, 48);
      c.on('pointertap', onTap);
      this.addChild(c);
      return c;
    };
    this.prev = arrowButton(false, handlers.prev);
    this.next = arrowButton(true, handlers.next);
    this.title = displayText('', 17);
    this.sub = new Text({
      text: '',
      style: uiStyle(12, COLOURS.cream, '600'),
    });
    this.sub.anchor.set(0.5);
    this.sub.alpha = 0.88;
    this.centre.addChild(this.title, this.sub);
    this.centre.eventMode = 'static';
    this.centre.cursor = 'pointer';
    this.centre.on('pointertap', handlers.open);
    this.addChild(this.centre);
  }

  get boardWidth(): number {
    return this.w;
  }

  /** The floor of the world it now names. */
  setFloor(texture: Texture | null): void {
    this.panel.setFloor(texture);
  }

  /** The floor under it, or null before it has one. */
  get floorTexture(): Texture | null {
    return this.panel.floorTexture;
  }

  set(name: string, sub: string): void {
    this.title.text = name;
    this.sub.text = sub;
    const room = this.w - 120;
    this.title.scale.set(Math.min(1, room / Math.max(1, this.title.width / this.title.scale.x)));
  }

  /** Lay out `width` wide, 56 tall, from the top-left. */
  layout(width: number): void {
    this.w = width;
    const h = 56;
    this.panel.resize(width, h);
    this.prev.position.set(30, h / 2);
    this.next.position.set(width - 30, h / 2);
    this.title.position.set(width / 2, 19);
    this.sub.position.set(width / 2, 40);
    this.centre.hitArea = new Rectangle(56, 0, width - 112, h);
    this.set(this.title.text, this.sub.text);
  }
}
